'use client'

import React, { useEffect, useState, useRef } from 'react';
import styles from './SearchDemo.module.css';
import {
  useSearchContext,
  SearchInput,
  SearchResults,
  SearchProvider,
  ActiveFiltersPanel,
  ValueFilterPanel,
  RangeFilterPanel,
  SortByPanel,
  SearchSettingsPanel,
  SearchResultRow
} from '@indxsearch/intrface';
import { Base, Button, Popover, Chip } from '@indxsearch/systm';
import { Sliders_horizontal, Indx } from '@indxsearch/pixl';

const fields = ['title', 'original_title', 'description', 'actors', 'release_year', 'vote_average', 'vote_count_tier', 'genres', 'release_year', 'decade', 'popularity', 'language'];

const filters = (
  <>
    <ActiveFiltersPanel />
    <ValueFilterPanel label="Genres" layout="grid" field="genres" preserveBlankFacetState={true} displayType="button" limit={10} />
    <ValueFilterPanel label="Actors" field="actors" displayType="checkbox" startCollapsed={false} limit={5} />
    <RangeFilterPanel label="Release Year" field="release_year" displayType="slider" expectedMin={1900} expectedMax={2025} />
    <ValueFilterPanel label="Decade" field="decade" displayType="checkbox" startCollapsed={true} preserveBlankFacetState={true} />
    <SortByPanel displayType="dropdown" />
    <RangeFilterPanel label="Rating" field="vote_average" displayType="slider" expectedMin={0} expectedMax={10} startCollapsed={true} />
    <ValueFilterPanel label="Vote Count" field="vote_count_tier" displayType="checkbox" startCollapsed={true} />
    <ValueFilterPanel label="Language" field="language" displayType="checkbox" startCollapsed={true} />
    <SearchSettingsPanel />
  </>
);

const renderResult = (item: any) => (
  <>
    <SearchResultRow variant="title">
      {item.title}
      {item.original_title && item.original_title !== item.title && (
        <span className={styles.secondaryText}>({item.original_title})</span>
      )}
      {item.release_year && (
        <Chip>{item.release_year}</Chip>
      )}
      {typeof item.vote_average === 'number' && (
        <Chip>{item.vote_average.toFixed(1)}</Chip>
      )}
    </SearchResultRow>

    {item.description && (
      <p className={styles.descriptionText}>{item.description}</p>
    )}

    {Array.isArray(item.genres) && item.genres.length > 0 && (
      <SearchResultRow>
        Genres:{' '}
        {item.genres.map((genre: string, idx: number) => (
          <Chip key={`${genre}-${idx}`}>{genre}</Chip>
        ))}
      </SearchResultRow>
    )}

    {Array.isArray(item.actors) && item.actors.length > 0 && (
      <SearchResultRow>
        Cast:{' '}
        {item.actors.slice(0, 5).map((actor: string, idx: number) => (
          <Chip key={`${actor}-${idx}`}>{actor}</Chip>
        ))}
        {item.actors.length > 5 && <Chip>+{item.actors.length - 5} more</Chip>}
      </SearchResultRow>
    )}

    {typeof item.vote_count_tier === 'number' && (
      <div className={styles.voteDisplay}>
        {item.vote_count_tier.toLocaleString()} votes
      </div>
    )}
  </>
);

interface SearchLayoutProps {
  onSearchFocus?: () => void
  onSearchBlur?: () => void
  onQueryChange?: (query: string) => void
}

function SearchLayout({ onSearchFocus, onSearchBlur, onQueryChange }: SearchLayoutProps = {}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showFilterButton, setShowFilterButton] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const { state, setSort, setSearchSettings } = useSearchContext();
  const { filters: activeFilters, rangeFilters } = state;

  const hasFilters =
    Object.keys(activeFilters).length > 0 ||
    Object.keys(rangeFilters).length > 0;

  // Set initial sort order and placeholder text
  useEffect(() => {
    setSort('popularity', false); // Sort by popularity, descending
    setSearchSettings({
      placeholderText: 'Search for a movie',
      coverageDepth: 2000
    });
  }, [setSort, setSearchSettings]);

  // Call onQueryChange when search query changes
  useEffect(() => {
    if (onQueryChange && state.query !== undefined) {
      onQueryChange(state.query);
    }
  }, [state.query, onQueryChange]);

  // RESIZE HANDLER
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver(entries => {
      for (let entry of entries) {
        const width = entry.contentRect.width;
        const isNarrow = width <= 800;
        setShowFilterButton(isNarrow);

        if (!isNarrow && filtersOpen) {
          setFiltersOpen(false);
        }
      }
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [filtersOpen]);

  return (
    <div className={styles.wrapper} ref={containerRef}>
      <Base className={styles.component}>
        <div className={styles.mainContent}>
          <div className={styles.header}>
            <SearchInput
              showFocus={false}
              className={styles.searchInput}
              onFocus={onSearchFocus}
              onBlur={onSearchBlur}
            />
            <div className={styles.meta}>
              {showFilterButton && (
                <div style={{ marginRight: '10px' }}>
                  <Popover
                    trigger={
                      <Button
                        variant={hasFilters ? 'primary' : 'secondary'}
                        iconRight={<Sliders_horizontal />}
                        size='micro'
                      >
                        Filters
                      </Button>
                    }
                    open={filtersOpen}
                    onOpenChange={setFiltersOpen}
                    align="end"
                    sideOffset={5}
                    className={styles.popoverContent}
                  >
                    <div className={styles.scrollFilters}>
                      {filters}
                    </div>
                  </Popover>
                </div>
              )}
              <span className={styles.logo}>
                <Indx size={28} color="var(--lv4)" />
              </span>
            </div>
          </div>
          <div className={styles.body}>
            <SearchResults fields={fields} resultsPerPage={5}>
              {renderResult}
            </SearchResults>
          </div>
        </div>
        <div className={styles.filtersColumn}>
          {filters}
        </div>
      </Base>
    </div>
  );
}

interface SearchDemoProps {
  onSearchFocus?: () => void
  onSearchBlur?: () => void
  onQueryChange?: (query: string) => void
}

export default function SearchDemo({ onSearchFocus, onSearchBlur, onQueryChange }: SearchDemoProps = {}) {
  const url = process.env.NEXT_PUBLIC_INDX_URL!;
  const token = process.env.NEXT_PUBLIC_INDX_TOKEN!;
  const dataset = process.env.NEXT_PUBLIC_INDX_DATASET!;

  return (
    <SearchProvider
      url={url}
      team="indx"
      dataset={dataset}
      preAuthenticatedToken={token}
      allowEmptySearch={true}
      enableFacets={true}
      maxResults={30}
      facetDebounceDelayMillis={200}
      enableDebugLogs={false}
    >
      <SearchLayout
        onSearchFocus={onSearchFocus}
        onSearchBlur={onSearchBlur}
        onQueryChange={onQueryChange}
      />
    </SearchProvider>
  );
}
